import styles from './CACReportModalContent.module.scss';
import ReportTable from '@components/ReportTable/ReportTable';
import { REPORT_MODAL_NAMES } from 'config/labels';
import React, { useMemo, useRef, useState, useLayoutEffect } from 'react';

export interface CACValue {
  value: {
    lm: { volume: number; score: number };
    lad: { volume: number; score: number };
    lcx: { volume: number; score: number };
    rca: { volume: number; score: number };
    total: { volume: number; score: number };
  };
}
interface Props {
  imageSrc?: { threeD?: string };
  value?: CACValue;
}
type RefName = 'HNR' | 'HOFF' | 'RAGGI' | 'RUMBERGER';
type Sex = 'Men' | 'Women';
type SeriesName = '90th' | '75th' | '50th' | '25th' | '10th';
interface RefDataset {
  title: string;
  ages: string[];
  series: Partial<Record<SeriesName, (number | null)[]>>;
}

export const DATA: Record<RefName, Record<Sex, RefDataset>> = {
  HNR: {
    Men: {
      title: 'Reference data: Men (HNR, Schmermund et al. 2006)',
      ages: ['45-49', '50-54', '55-59', '60-64', '65-69', '70-74'],
      series: {
        '10th': [null, null, null, null, 2.8, 7.0],
        '25th': [null, null, 6.7, 11.6, 21.9, 51.8],
        '50th': [4.8, 12.4, 51.6, 83.3, 125.3, 200.6],
        '75th': [60.9, 78.6, 190.8, 309.7, 394.5, 734.8],
        '90th': [237.0, 266.6, 440.9, 853.5, 1195.4, 1771.8],
      },
    },
    Women: {
      title: 'Reference data: Women (HNR, Schmermund et al. 2006)',
      ages: ['45-49', '50-54', '55-59', '60-64', '65-69', '70-74'],
      series: {
        '10th': [null, null, null, null, null, null],
        '25th': [null, null, null, null, null, 1.8],
        '50th': [null, null, null, 3.5, 12.5, 43.2],
        '75th': [2.6, 5.4, 20.1, 40.0, 101.9, 209.5],
        '90th': [32.7, 38.2, 105.2, 174.0, 305.3, 716.6],
      },
    },
  },
  HOFF: {
    Men: {
      title: 'Reference data: Men (Hoff et al. 2001)',
      ages: [
        '<40',
        '40-44',
        '45-49',
        '50-54',
        '55-59',
        '60-64',
        '65-69',
        '70-74',
        '>74',
      ],
      series: {
        '25th': [null, null, null, 1, 4, 13, 32, 64, 166],
        '50th': [1, 1, 3, 15, 48, 113, 180, 310, 473],
        '75th': [3, 9, 36, 103, 215, 410, 566, 892, 1071],
        '90th': [14, 59, 154, 332, 554, 994, 1299, 1774, 1982],
      },
    },
    Women: {
      title: 'Reference data: Women (Hoff et al. 2001)',
      ages: [
        '<40',
        '40-44',
        '45-49',
        '50-54',
        '55-59',
        '60-64',
        '65-69',
        '70-74',
        '>74',
      ],
      series: {
        '25th': [null, null, null, null, null, null, 1, 3, 9],
        '50th': [null, null, null, null, 1, 3, 24, 52, 75],
        '75th': [1, 1, 2, 5, 23, 57, 145, 210, 241],
        '90th': [3, 4, 22, 55, 121, 193, 410, 631, 709],
      },
    },
  },
  RAGGI: {
    Men: {
      title: 'Reference data: Men (Raggi et al. 2000)',
      ages: ['35-39', '40-44', '45-49', '50-54', '55-59', '60-64', '65-70'],
      series: {
        '25th': [null, null, null, null, 3, 14, 28],
        '50th': [null, null, 3, 16, 41, 118, 151],
        '75th': [2, 11, 44, 101, 187, 434, 569],
        '90th': [21, 64, 176, 320, 502, 804, 1178],
      },
    },
    Women: {
      title: 'Reference data: Women (Raggi et al. 2000)',
      ages: ['35-39', '40-44', '45-49', '50-54', '55-59', '60-64', '65-70'],
      series: {
        '25th': [null, null, null, null, null, null, null],
        '50th': [null, null, null, null, null, 4, 24],
        '75th': [null, null, null, 10, 33, 87, 123],
        '90th': [4, 9, 23, 66, 140, 310, 362],
      },
    },
  },
  RUMBERGER: {
    Men: {
      title: 'Reference data: Men (Rumberger et al. 1999)',
      ages: ['40-49', '50-59', '60-69'],
      series: {
        '10th': [null, null, 1],
        '25th': [null, 2, 15],
        '50th': [1, 30, 100],
        '75th': [10, 150, 325],
        '90th': [40, 380, 700],
      },
    },
    Women: {
      title: 'Reference data: Women (Rumberger et al. 1999)',
      ages: ['40-49', '50-59', '60-69'],
      series: {
        '10th': [null, null, null],
        '25th': [null, null, 1],
        '50th': [null, null, 20],
        '75th': [1, 3, 80],
        '90th': [10, 40, 118],
      },
    },
  },
};

type SeriesMask = Partial<Record<SeriesName, [start: number, end: number]>>;
const MASKS: Partial<Record<RefName, Partial<Record<Sex, SeriesMask>>>> = {
  HNR: {
    Men: { '25th': [1, 5], '10th': [3, 5] },
    Women: { '50th': [3, 5], '25th': [4, 5] },
  },
  RAGGI: {
    Men: { '75th': [1, 6], '50th': [1, 6] },
    Women: { '75th': [1, 6], '50th': [1, 6] },
  },
};
let currentRef: RefName = 'HNR';
let currentSex: Sex = 'Men';
function applyMask(dataset: RefDataset): RefDataset {
  const m = (MASKS as any)[currentRef]?.[currentSex] as SeriesMask | undefined;
  const agesLen = dataset.ages.length;
  const series: any = {};
  (Object.keys(dataset.series) as SeriesName[]).forEach((k) => {
    let arr = (dataset.series[k] || []).slice();
    for (let i = 0; i < agesLen; i++) if (arr[i] === undefined) arr[i] = null;
    const rng = m?.[k];
    if (rng) {
      for (let i = 0; i < agesLen; i++)
        if (i < rng[0] || i > rng[1]) arr[i] = null;
    }
    series[k] = arr;
  });
  return { ...dataset, series };
}

const COLORS: Partial<Record<SeriesName, string>> = {
  '90th': '#ff4d4d',
  '75th': '#f0a020',
  '50th': '#a155d3',
  '25th': '#58a6ff',
  '10th': '#2dd4bf',
};
const ORDER: SeriesName[] = ['90th', '75th', '50th', '25th', '10th'];
const TICKS_Y = [1, 10, 100, 1000, 10000];
const buildMinorTicks = (min = 1, max = 10000) => {
  const out: number[] = [];
  for (let decade = 1; decade < max; decade *= 10) {
    for (let m = 2; m <= 9; m++) {
      const v = decade * m;
      if (v >= min && v <= max) out.push(v);
    }
  }
  return out;
};
const MINOR_TICKS_Y = buildMinorTicks(1, 10000);
const splitSegmentsWithIndex = (arr: (number | null)[]) => {
  const segs: { idx: number[]; vals: number[] }[] = [];
  let ci: number[] = [];
  let cv: number[] = [];
  arr.forEach((v, i) => {
    if (v == null || Number.isNaN(v)) {
      if (cv.length > 0) {
        segs.push({ idx: ci, vals: cv });
        ci = [];
        cv = [];
      }
    } else {
      ci.push(i);
      cv.push(v);
    }
  });
  if (cv.length > 0) segs.push({ idx: ci, vals: cv });
  return segs;
};
function getRiskCategory(scoreValue: number) {
  if (scoreValue === 0) return 0;
  if (scoreValue <= 10) return 1;
  if (scoreValue <= 100) return 2;
  if (scoreValue <= 400) return 3;
  return 4;
}

function useElementSizeScale(ref: React.RefObject<HTMLElement>) {
  const [size, setSize] = useState({ w: 800, h: 400 });
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({
        w: Math.max(300, width),
        h: Math.max(220, height),
      });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);

  const scale = Math.max(0.75, Math.min(1.5, size.w / 1000));
  return { size, scale };
}

function ReferenceChart({
  dataset,
  totalScore,
  width,
  height,
  scale,
}: {
  dataset: RefDataset;
  totalScore: number;
  width: number;
  height: number;
  scale: number;
}) {
  const W = width;
  const outerH = height;
  const legendRef = useRef<HTMLDivElement>(null);
  const [legendH, setLegendH] = useState(0);

  useLayoutEffect(() => {
    if (!legendRef.current) return;
    setLegendH(legendRef.current.getBoundingClientRect().height);
  }, [dataset.title, scale, dataset.ages.length]);

  const H = Math.max(0, outerH - legendH);

  const pad = {
    top: 10 * scale,
    right: 10 * scale,
    bottom: Math.min(100 * scale, Math.max(40 * scale, H * 0.2)),
    left: 70 * scale,
  };
  const innerW = W - pad.left - pad.right;
  const innerH = Math.max(1, H - pad.top - pad.bottom);

  const n = dataset.ages.length;
  const xCenter = (i: number) => ((i + 0.5) / n) * innerW;
  const logY = (v: number, min = 1, max = 10000) => {
    const vv = Math.max(min, Math.min(max, v));
    return (
      (Math.log10(vv) - Math.log10(min)) / (Math.log10(max) - Math.log10(min))
    );
  };
  const yPos = (v: number) => (1 - logY(v)) * innerH;

  const totalY =
    Number.isFinite(totalScore) && totalScore > 0
      ? pad.top + yPos(totalScore)
      : null;

  return (
    <div className={`${styles['chart-wrap']}`} style={{ height: `${outerH}` }}>
      <div
        ref={legendRef}
        className={styles['chart-legend']}
        style={{ fontSize: `${10 * scale}px` }}
      >
        <div
          className={styles['chart-title']}
          style={{ fontSize: `${14 * scale}px` }}
        >
          {dataset.title}
        </div>
        <div className={styles['legend-row']}>
          {ORDER.filter((k) => dataset.series[k]?.length).map((k) => (
            <span key={k} className={styles['legend-item']}>
              <span
                className={styles['legend-swatch']}
                style={{ background: COLORS[k] }}
              />
              {k}
            </span>
          ))}
        </div>
      </div>

      <div className={styles['chart-inner']} style={{ height: `${H}` }}>
        <svg
          className="chart"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
        >
          {/* <rect x={0} y={0} width={W} height={H} fill="#0D0D0D" /> */}

          {/* minor ticks */}
          {MINOR_TICKS_Y.map((t, idx) => {
            const y = pad.top + yPos(t);
            return (
              <line
                key={`gmy-${idx}`}
                x1={pad.left}
                y1={y}
                x2={W - pad.right}
                y2={y}
                stroke="rgba(255,255,255,.10)"
                strokeWidth={1}
              />
            );
          })}

          {/* major ticks */}
          {TICKS_Y.map((t, idx) => {
            const y = pad.top + yPos(t);
            return (
              <g key={`gy-${idx}`}>
                <line
                  x1={pad.left}
                  y1={y}
                  x2={W - pad.right}
                  y2={y}
                  stroke="rgba(255,255,255,.12)"
                  strokeWidth={1}
                />
                <text
                  x={pad.left - 12 * scale}
                  y={y}
                  textAnchor="end"
                  className={styles['y-label']}
                  style={{ fontSize: `${14 * scale}px` }}
                >
                  {t.toLocaleString()}
                </text>
              </g>
            );
          })}

          {/* X axis */}
          <line
            x1={pad.left}
            y1={H - pad.bottom}
            x2={W - pad.right}
            y2={H - pad.bottom}
            stroke="rgba(255,255,255,.25)"
            strokeWidth={1}
          />
          {dataset.ages.map((lab, i) => (
            <text
              key={i}
              x={pad.left + xCenter(i)}
              y={H - pad.bottom + 14 * scale}
              textAnchor="middle"
              className={styles['x-label']}
              style={{ fontSize: `${14 * scale}px` }}
            >
              {lab}
            </text>
          ))}

          {/* axis titles */}
          <text
            x={W / 2}
            y={H - pad.bottom + 44 * scale}
            textAnchor="middle"
            className={styles['axis-title']}
            style={{ fontSize: `${14 * scale}px` }}
          >
            Age
          </text>
          <text
            transform={`translate(${10 * scale} ${pad.top + innerH / 2}) rotate(-90)`}
            textAnchor="middle"
            className={styles['axis-title']}
            style={{ fontSize: `${14 * scale}px` }}
          >
            Coronary calcium score (log)
          </text>

          {/* series */}
          {ORDER.filter((k) => dataset.series[k]?.length).map((k) => {
            const vals = dataset.series[k]!;
            const segs = splitSegmentsWithIndex(vals);
            return (
              <g key={k}>
                {segs.map(({ idx, vals: seg }, sidx) => {
                  if (seg.length < 2) return null;
                  const pts = seg
                    .map(
                      (v, j) =>
                        `${pad.left + xCenter(idx[j])},${pad.top + yPos(v)}`,
                    )
                    .join(' ');
                  return (
                    <polyline
                      key={sidx}
                      fill="none"
                      stroke={COLORS[k]}
                      strokeWidth={2}
                      points={pts}
                    />
                  );
                })}
                {vals.map((v, i) =>
                  v == null ? null : (
                    <circle
                      key={i}
                      cx={pad.left + xCenter(i)}
                      cy={pad.top + yPos(v)}
                      r={3.2 * scale}
                      fill={COLORS[k]}
                    />
                  ),
                )}
              </g>
            );
          })}

          {/* total line */}
          {totalY !== null && (
            <line
              x1={pad.left}
              x2={W - pad.right}
              y1={totalY}
              y2={totalY}
              stroke="#ff3b3b"
              strokeWidth={2}
            />
          )}
        </svg>
      </div>
    </div>
  );
}

export default function CACReportModalContent({ imageSrc, value }: Props) {
  const names = REPORT_MODAL_NAMES.cac;
  const totalScore = value?.value.total?.score ?? 0;

  const [refName, setRefName] = useState<RefName>('HNR');
  const [sex, setSex] = useState<Sex>('Men');

  const dataset = useMemo(() => {
    currentRef = refName;
    currentSex = sex;
    return applyMask(DATA[refName][sex]);
  }, [refName, sex]);

  const chartSlotRef = useRef<HTMLDivElement>(null);
  const { size, scale } = useElementSizeScale(chartSlotRef);

  return (
    <div className={styles['main-container']}>
      <div className={styles['content-container']}>
        <div className={styles['content-row']}>
          <div className={styles['trd-model-container']}>
            <div className={styles['footer-toggle']}>
              <div className={styles['line99']} />
              <span>{names.sections.names.threedmodel}</span>
            </div>
            <img src={imageSrc?.threeD} />
          </div>
          <div className={styles['chart-panel']}>
            <div className={styles['image-container']}>
              <div className={styles['chart-container']}>
                <div className={styles['footer-toggle']}>
                  <div className={styles['line99']} />
                  <span>
                    {`Reference`}
                    <select
                      className={styles['select']}
                      value={refName}
                      onChange={(e) => setRefName(e.target.value as RefName)}
                      onWheel={(e) => {
                        const nameArr: RefName[] = [
                          'HNR',
                          'HOFF',
                          'RAGGI',
                          'RUMBERGER',
                        ];
                        const index = nameArr.findIndex((r) => refName === r);
                        const nextIndex = index + Math.sign(e.deltaY);
                        if (nextIndex < 0 || nextIndex >= nameArr.length)
                          return;
                        setRefName(nameArr[nextIndex]);
                      }}
                    >
                      <option value="HNR">HNR</option>
                      <option value="HOFF">HOFF</option>
                      <option value="RAGGI">RAGGI</option>
                      <option value="RUMBERGER">RUMBERGER</option>
                    </select>
                    <select
                      className={styles['select']}
                      value={sex}
                      onChange={(e) => setSex(e.target.value as Sex)}
                      onWheel={(e) => {
                        const nameArr: Sex[] = ['Men', 'Women'];
                        const index = nameArr.findIndex((r) => sex === r);
                        const nextIndex = index + Math.sign(e.deltaY);
                        if (nextIndex < 0 || nextIndex >= nameArr.length)
                          return;
                        setSex(nameArr[nextIndex]);
                      }}
                    >
                      <option value="Men">Men</option>
                      <option value="Women">Women</option>
                    </select>
                  </span>
                </div>

                {/* <div className={styles['chart-slot']}> */}
                <ReferenceChart
                  dataset={dataset}
                  totalScore={totalScore}
                  width={size.w}
                  height={size.h}
                  scale={scale}
                />
                {/* </div> */}
              </div>
            </div>

            <div className={styles['container']}>
              <div className={styles['container1']}>
                <div className={styles['footer-toggle']}>
                  <div className={styles['line99']} />
                  <span>{names.sections.names.calciumscore}</span>
                </div>
                {/* <div className="table-scroll"> */}
                <ReportTable
                  className="cac-report1"
                  highlightRowIndex={4}
                  columns={names.sections.calciumScore.columns}
                  rows={[
                    {
                      label: names.sections.calciumScore.labels.lm,
                      values: [
                        value?.value.lm?.volume.toString() ?? '-',
                        value?.value.lm?.score.toString() ?? '-',
                      ],
                    },
                    {
                      label: names.sections.calciumScore.labels.lad,
                      values: [
                        value?.value.lad?.volume.toString() ?? '-',
                        value?.value.lad?.score.toString() ?? '-',
                      ],
                    },
                    {
                      label: names.sections.calciumScore.labels.lcx,
                      values: [
                        value?.value.lcx?.volume.toString() ?? '-',
                        value?.value.lcx?.score.toString() ?? '-',
                      ],
                    },
                    {
                      label: names.sections.calciumScore.labels.rca,
                      values: [
                        value?.value.rca?.volume.toString() ?? '-',
                        value?.value.rca?.score.toString() ?? '-',
                      ],
                    },
                    {
                      label: names.sections.calciumScore.labels.total,
                      values: [
                        value?.value.total?.volume.toString() ?? '-',
                        value?.value.total?.score.toString() ?? '-',
                      ],
                    },
                  ]}
                />
                {/* </div> */}
              </div>

              {/* <div className={styles['container2']}>
              <div className={styles['footer-toggle']}>
                <div className={styles['line99']}/>
                <span>{names.sections.names.calciumriskclassification}</span>
              </div>
                <ReportTable
                  className='cac-report2'
                  highlightRowIndex={Number(getRiskCategory(totalScore))}
                  columns={names.sections?.agatston?.columns ?? []}
                  rows={Object.entries(names.sections?.agatston?.labels ?? {}).map(([, label]) => {
                    const [score, risk] = label.split(' - ');
                    return { label: score.trim(), values: [risk?.trim() ?? '-'] };
                  })}
                />
            </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
